import { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Route, Routes, Outlet, Navigate } from 'react-router'
import { ToastContainer } from 'react-toastify'
import styled from '@emotion/styled'
import { ThemeProvider } from '@mui/material/styles'
import { useAppDispatch, useAppSelector } from 'src/redux/app/app.hooks'
import { AuthProvider } from 'src/components/app/AuthProvider'
import * as routes from 'src/constants/routes'
import { getAllZones } from 'src/api'
import { setZoneOptions, setAllZones } from 'src/redux/zone'
import { selectIsAuthenticated, selectAuthError } from 'src/redux/auth'
import { GlobalErrorBoundary } from 'src/components/parts/errorBoundaries/GlobalErrorBoundary'
import { ImportErrorBoundary } from 'src/components/parts/errorBoundaries/ImportErrorBoundary'
import Navigation from 'src/components/parts/navigation/Navigation'
import Header from 'src/components/parts/navigation/Header'
import { Login } from 'src/pages/login/Login'
import { H5 } from 'src/ui-kit/headings/Headings'
import { ContentWrapper } from 'src/components/app/ContentWrapper'
import NotFound from 'src/pages/notFound/NotFound'
import { getOrganizations } from 'src/redux/hunter'
import { getPermissionsForRoles } from 'src/redux/permissionsForRoles'
import { fontPrimary } from 'src/ui-kit/fonts/fontFamily'
import { zIndex } from 'src/constants/zIndex'
import { QueryClientProvider } from 'react-query'
import { queryClient } from 'src/api/axios'
import BatteryContextLayout from 'src/pages/batteries/ContextLayout'
import { getAppTheme } from 'src/utils/theme/themeUtils'

const appTheme = getAppTheme()

export const App = () => {
    const CityRegs = lazy(() =>
        import('src/pages/cityRegs/CityRegs').then(module => ({
            default: module.CityRegs,
        })),
    )
    const Confighub = lazy(() =>
        import('src/pages/confighub/Confighub').then(module => ({
            default: module.Confighub,
        })),
    )
    const Riders = lazy(() =>
        import('src/pages/riders/Riders').then(module => ({
            default: module.Riders,
        })),
    )
    const AccidentReports = lazy(() =>
        import('src/pages/accidentReports/AccidentReports').then(module => ({
            default: module.AccidentReports,
        })),
    )
    const RideInspector = lazy(() =>
        import('src/pages/rideInspector/RideInspector').then(module => ({
            default: module.RideInspector,
        })),
    )
    const Hunters = lazy(() =>
        import('src/pages/hunters/Hunters').then(module => ({
            default: module.Hunters,
        })),
    )
    const Vehicles = lazy(() =>
        import('src/pages/vehicles/Vehicles').then(module => ({
            default: module.Vehicles,
        })),
    )
    const ParkingPhotoLabelling = lazy(() =>
        import('src/pages/parkingPhotoLabelling/ParkingPhotoLabelling').then(module => ({
            default: module.ParkingPhotoLabelling,
        })),
    )
    const SafetyControlSystem = lazy(() =>
        import('src/pages/safetyControlSystem/SafetyControlSystem').then(module => ({
            default: module.SafetyControlSystem,
        })),
    )
    const Vmap = lazy(() =>
        import('src/pages/vmap/Vmap').then(module => ({
            default: module.Vmap,
        })),
    )
    const FleetConnectivity = lazy(() =>
        import('src/pages/fleetConnectivity/FleetConnectivity').then(module => ({
            default: module.FleetConnectivity,
        })),
    )
    const Zones = lazy(() =>
        import('src/pages/zones/Zones').then(module => ({
            default: module.Zones,
        })),
    )
    const Batteries = lazy(() =>
        import('src/pages/batteries/Batteries').then(module => ({
            default: module.Batteries,
        })),
    )
    const Battery = lazy(() =>
        import('src/pages/battery/Battery').then(module => ({
            default: module.Battery,
        })),
    )
    const BatteryBulk = lazy(() =>
        import('src/pages/batteryBulk/BatteryBulk').then(module => ({
            default: module.BatteryBulk,
        })),
    )
    const BatteryShipments = lazy(() =>
        import('src/pages/batteries/Shipments').then(module => ({
            default: module.Shipments,
        })),
    )
    const Campaigns = lazy(() =>
        import('src/pages/campaigns/Campaigns').then(module => ({
            default: module.Campaigns,
        })),
    )
    const Overview = lazy(() =>
        import('src/pages/overview/Overview').then(module => ({
            default: module.Overview,
        })),
    )
    const Permissions = lazy(() =>
        import('src/pages/permissions/Permissions').then(module => ({
            default: module.Permissions,
        })),
    )
    const Wallet = lazy(() =>
        import('src/pages/wallet/Wallet').then(module => ({
            default: module.Wallet,
        })),
    )
    const Organizations = lazy(() =>
        import('src/pages/organizations/Organizations').then(module => ({
            default: module.Organizations,
        })),
    )
    const RiderDetails = lazy(() =>
        import('src/pages/riderDetails/RiderDetails').then(module => ({
            default: module.RiderDetails,
        })),
    )
    const ScooterDetails = lazy(() =>
        import('src/pages/scooterDetails/VehicleDetails').then(module => ({
            default: module.VehicleDetails,
        })),
    )
    const HunterDetails = lazy(() =>
        import('src/pages/hunterDetails/HunterDetails').then(module => ({
            default: module.HunterDetails,
        })),
    )
    const CreateOrganization = lazy(() =>
        import('src/pages/createOrganization/CreateOrganization').then(module => ({
            default: module.CreateOrganization,
        })),
    )
    const RepairManagement = lazy(() =>
        import('src/pages/repairManagement/RepairManagement').then(module => ({
            default: module.RepairManagement,
        })),
    )
    const RepairManagementDetails = lazy(() =>
        import('src/pages/repairManagementDetails/RepairManagementDetails').then(module => ({
            default: module.RepairManagementDetails,
        })),
    )
    const RepairManagementVehicles = lazy(() =>
        import('src/pages/repairManagementVehicles/RepairManagementVehicles').then(module => ({
            default: module.RepairManagementVehicles,
        })),
    )
    const VehicleReports = lazy(() =>
        import('src/pages/vehicleReports/VehicleReports').then(module => ({
            default: module.VehicleReports,
        })),
    )
    const PricingPortal = lazy(() =>
        import('src/pages/pricingPortal/PricingPortal').then(module => ({
            default: module.PricingPortal,
        })),
    )
    const PartnerPortal = lazy(() =>
        import('src/pages/partnerPortal/PartnerPortal').then(module => ({
            default: module.PartnerPortal,
        })),
    )

    const VehicleReportDetails = lazy(() =>
        import('src/pages/vehicleReportDetails/VehicleReportDetails').then(module => ({
            default: module.VehicleReportDetails,
        })),
    )

    const VoucherDetails = lazy(() =>
        import('src/pages/vouchersDetails/VoucherDetails').then(module => ({
            default: module.VoucherDetails,
        })),
    )

    const Warehouses = lazy(() =>
        import('src/pages/warehouses/Warehouses').then(module => ({
            default: module.Warehouses,
        })),
    )

    const MaintenanceNeeds = lazy(() =>
        import('src/pages/maintenanceNeeds/MaintenanceNeeds').then(module => ({
            default: module.MaintenanceNeeds,
        })),
    )

    const WorkAreas = lazy(() =>
        import('src/pages/workAreas/WorkAreas').then(module => ({
            default: module.WorkAreas,
        })),
    )

    const Analytics = lazy(() =>
        import('src/pages/analytics/Analytics').then(module => ({
            default: module.Analytics,
        })),
    )

    const VoiAgent = lazy(() =>
        import('src/pages/voiAgent/VoiAgent').then(module => ({
            default: module.VoiAgent,
        })),
    )

    const StationCleanupJobs = lazy(() =>
        import('src/pages/stationCleanupJobs/StationCleanupJobs').then(module => ({
            default: module.StationCleanupJobs,
        })),
    )

    const isAuthenticated = useAppSelector(selectIsAuthenticated)
    const authError = useAppSelector(selectAuthError)

    const dispatch = useAppDispatch()

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(getPermissionsForRoles())
            getAllZones()
                .then(res => {
                    if (!(res instanceof Error)) {
                        const zoneOptions = res
                            .map(zone => ({ value: zone.zone_id, label: zone.name, currency: zone.currency }))
                            .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))
                        dispatch(setZoneOptions(zoneOptions))

                        dispatch(setAllZones(res))
                    }
                    dispatch(getOrganizations()) // TODO: Remove this and let the respective components ask for this data
                })
                .catch(e => console.log(e))
        }
    }, [isAuthenticated, dispatch])

    if (authError) {
        return (
            <NoAccessWarnWrapper data-testid='no-token-warning'>
                <H5>{authError}</H5>
            </NoAccessWarnWrapper>
        )
    }

    return (
        <GlobalErrorBoundary>
            <ImportErrorBoundary>
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <AuthProvider>
                            <ThemeProvider theme={appTheme}>
                                <Routes>
                                    <Route path={routes.LOGIN} element={<Login />} />
                                    <Route
                                        path='*'
                                        element={
                                            isAuthenticated && (
                                                <MainWrapper>
                                                    <Header />
                                                    <Navigation />
                                                    <Suspense fallback={<ContentWrapper>Loading...</ContentWrapper>}>
                                                        <Routes>
                                                            <Route
                                                                path={`${routes.CONFIGHUB}/*`}
                                                                element={<Confighub />}
                                                            />
                                                            <Route
                                                                path={`${routes.CITYREGS}/*`}
                                                                element={<CityRegs />}
                                                            />
                                                            <Route
                                                                path={`${routes.RIDE_INSPECTOR}/*`}
                                                                element={<RideInspector />}
                                                            />
                                                            <Route
                                                                path={`${routes.PRICING_PORTAL}/*`}
                                                                element={<PricingPortal />}
                                                            />
                                                            <Route
                                                                path={`${routes.PARTNER_PORTAL}/*`}
                                                                element={<PartnerPortal />}
                                                            />
                                                            <Route
                                                                element={
                                                                    <ContentWrapper>
                                                                        <Outlet />
                                                                    </ContentWrapper>
                                                                }
                                                            >
                                                                <Route path={routes.OVERVIEW} element={<Overview />} />
                                                                <Route
                                                                    path={`${routes.ORG}/:id`}
                                                                    element={<Organizations />}
                                                                />
                                                                <Route
                                                                    path={routes.CREATE_ORG}
                                                                    element={<CreateOrganization />}
                                                                />
                                                                <Route path={routes.HUNTERS} element={<Hunters />} />
                                                                <Route
                                                                    path={`${routes.HUNTERS}/:id`}
                                                                    element={<HunterDetails />}
                                                                />
                                                                <Route path={routes.VEHICLES} element={<Vehicles />} />
                                                                <Route
                                                                    path={routes.PARKING_PHOTO_LABELLING}
                                                                    element={<ParkingPhotoLabelling />}
                                                                />
                                                                <Route
                                                                    path={routes.SAFETY_CONTROL_SYSTEM}
                                                                    element={<SafetyControlSystem />}
                                                                />
                                                                <Route
                                                                    path={`${routes.VEHICLES}/:id`}
                                                                    element={<ScooterDetails />}
                                                                />
                                                                <Route path={routes.VMAP} element={<Vmap />} />
                                                                <Route
                                                                    path={routes.FLEET_CONNECTIVITY}
                                                                    element={<FleetConnectivity />}
                                                                />
                                                                <Route path={`${routes.ZONES}/*`} element={<Zones />} />
                                                                <Route
                                                                    path={routes.LEGAL}
                                                                    element={
                                                                        <Navigate
                                                                            to={`${routes.CITYREGS}/legal-entities`}
                                                                            replace
                                                                        />
                                                                    }
                                                                />
                                                                <Route
                                                                    path={routes.CAMPAIGNS}
                                                                    element={<Campaigns />}
                                                                />
                                                                <Route element={<BatteryContextLayout />}>
                                                                    <Route
                                                                        path={routes.BATTERIES}
                                                                        element={<Batteries />}
                                                                    />
                                                                    <Route
                                                                        path={`${routes.BATTERIES}/:serialNo`}
                                                                        element={<Battery />}
                                                                    />
                                                                    <Route
                                                                        path={routes.BATTERY_BULK}
                                                                        element={<BatteryBulk />}
                                                                    />
                                                                    <Route
                                                                        path={routes.BATTERY_SHIPMENTS}
                                                                        element={<BatteryShipments />}
                                                                    />
                                                                </Route>
                                                                <Route
                                                                    path={routes.PERMISSIONS}
                                                                    element={<Permissions />}
                                                                />
                                                                <Route
                                                                    path={`${routes.WALLET}/:id`}
                                                                    element={<Wallet />}
                                                                />
                                                                <Route
                                                                    path={`${routes.RIDERS}/:id`}
                                                                    element={<RiderDetails />}
                                                                />
                                                                <Route
                                                                    path={`${routes.RIDERS}/:id/vouchers`}
                                                                    element={<VoucherDetails />}
                                                                />
                                                                <Route path={routes.RIDERS} element={<Riders />} />
                                                                <Route
                                                                    path={`${routes.ACCIDENT_REPORTS}/*`}
                                                                    element={<AccidentReports />}
                                                                />
                                                                <Route
                                                                    path={routes.REPAIR_MANAGEMENT}
                                                                    element={<RepairManagement />}
                                                                />
                                                                <Route
                                                                    path={`${routes.REPAIR_MANAGEMENT}/:id`}
                                                                    element={<RepairManagementDetails />}
                                                                />
                                                                <Route
                                                                    path={routes.ADMINISTRATION}
                                                                    element={<RepairManagementVehicles />}
                                                                />
                                                                <Route
                                                                    path={routes.WAREHOUSES}
                                                                    element={<Warehouses />}
                                                                />
                                                                <Route
                                                                    path={routes.MAINTENANCE_NEEDS}
                                                                    element={<MaintenanceNeeds />}
                                                                />
                                                                <Route
                                                                    path={routes.ANALYTICS}
                                                                    element={<Analytics />}
                                                                />
                                                                <Route path={routes.VOI_AGENT} element={<VoiAgent />} />
                                                                <Route
                                                                    path={`${routes.VEHICLE_REPORTS}/:id`}
                                                                    element={<VehicleReportDetails />}
                                                                />
                                                                <Route
                                                                    path={routes.VEHICLE_REPORTS}
                                                                    element={<VehicleReports />}
                                                                />
                                                                <Route
                                                                    path={routes.WORK_AREAS}
                                                                    element={<WorkAreas />}
                                                                />
                                                                <Route
                                                                    path={routes.STATION_CLEANUP_JOBS}
                                                                    element={<StationCleanupJobs />}
                                                                />
                                                                <Route path='*' element={<NotFound />} />
                                                            </Route>
                                                        </Routes>
                                                    </Suspense>
                                                    <ToastContainer
                                                        autoClose={5000}
                                                        theme='colored'
                                                        position='bottom-right'
                                                        icon={false}
                                                    />
                                                </MainWrapper>
                                            )
                                        }
                                    />
                                </Routes>
                            </ThemeProvider>
                        </AuthProvider>
                    </BrowserRouter>
                </QueryClientProvider>
            </ImportErrorBoundary>
        </GlobalErrorBoundary>
    )
}

const NoAccessWarnWrapper = styled.div`
    background-color: #ed6a5a;
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    flex-direction: column;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${zIndex.priority};
    padding: 40px;
`

const MainWrapper = styled.div`
    box-sizing: border-box;
    display: flex;
    ${fontPrimary}
    width: 100vw;
    height: 100vh;

    /* Add padding-top to not cover header */
    padding-top: 70px;
`
